import { FC, useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { chatToClaimItem, ChatToListItem } from '~/components/Chat/ChatHelper';
import { useClaimChatsContext } from '~/components/Chat/context/ChatContext.tsx';
import { useChatDocumentContext } from '~/components/Chat/context/ChatDocumentContext.tsx';
import { ChatListItem } from '~/components/Chat/types';
import { useActionsContext } from '~/components/Claim/Actions/ActionsContext';
import { actionToListItem } from '~/components/Claim/Actions/ActionsHelper';
import { Action } from '~/components/Claim/Actions/types';
import { useClaimContext } from '~/components/Claim/ClaimContext';
import ClaimListPanel from '~/components/Claim/ClaimListPanel/ClaimListPanel';
import { EMPTY_STATES_BY_TYPE } from '~/components/Claim/ClaimListPanel/ClaimListPanelEmptyStates';
import { documentToClaimItem, documentToListItem } from '~/components/Claim/Documents/DocumentsHelper';
import { useInsightsContext } from '~/components/Claim/Insights/InsightsContext';
import { insightToListItem } from '~/components/Claim/Insights/InsightsHelper';
import { Insight } from '~/components/Claim/Insights/types';
import ActionSidePanel from '~/components/Claim/SidePanels/ActionSidePanel';
import DocumentSidePanel from '~/components/Claim/SidePanels/DocumentSidePanel';
import InsightSidePanel from '~/components/Claim/SidePanels/InsightSidePanel';
import { ClaimItemBase, ClaimItemType } from '~/components/Claim/types';
import { ResizeableHandle, ResizeablePanel, ResizeablePanels } from '~/core/atoms/ResizeablePanels';
import cn from '~/utils/cn';

const EFFECT_CONF = {
  // both keys should have same duration value
  DURATION: 300,
  CSS_DURATION: 'duration-300',
};

const ClaimPageBody: FC = () => {
  const navigate = useNavigate();
  const { claimNumber } = useClaimContext();
  const { actions, isActionsLoading, actionsError, fetchActions } = useActionsContext();
  const { insights, isInsightsLoading, insightsError, fetchInsights } = useInsightsContext();
  const { chats, isChatsLoading, chatsError, fetchChats } = useClaimChatsContext();
  const {
    documents,
    getClaimDocuments,
    isLoading: isDocumentsLoading,
    error: documentsError,
  } = useChatDocumentContext();

  const [selectedItem, setSelectedItem] = useState<ClaimItemBase | null>(null);
  const [selectedItemType, setSelectedItemType] = useState<ClaimItemType | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const chatItems: ClaimItemBase[] = chats
    .filter((chat: ChatListItem) => {
      const source = chat.chat_metadata?.source as string;
      // Configure which sources to exclude (hide from users)
      const excludedSources = ['Simulate Chat Exchange'];
      return !excludedSources.includes(source);
    })
    .map(chatToClaimItem);

  const documentItems: ClaimItemBase[] = Object.values(documents).map(documentToClaimItem);

  useEffect(() => {
    if (claimNumber) {
      fetchActions(claimNumber);
      fetchInsights(claimNumber);
      fetchChats(claimNumber);
      getClaimDocuments();
    }
  }, [claimNumber, fetchActions, fetchInsights, fetchChats, getClaimDocuments]);

  useEffect(() => {
    if (selectedItem) {
      setIsAnimating(true);
    } else {
      setIsAnimating(false);
    }
  }, [selectedItem]);

  const closePanel = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setSelectedItem(null);
      setSelectedItemType(null);
    }, EFFECT_CONF.DURATION);
  };

  const SIDE_PANELS_MAP = {
    insight: (item: ClaimItemBase) => <InsightSidePanel insight={item as Insight} onClose={closePanel} />,
    action: (item: ClaimItemBase) => <ActionSidePanel actionId={item.id} onClose={closePanel} />,
    document: (item: ClaimItemBase) => <DocumentSidePanel document={item} onClose={closePanel} />,
  };

  const toggleSelectedItem = (item: ClaimItemBase, type: ClaimItemType) => {
    if (selectedItem?.id === item.id) {
      closePanel();
      return;
    }
    setSelectedItem(item);
    setSelectedItemType(type);
  };

  const handleChatItemClick = (chatItem: ClaimItemBase) => {
    if (claimNumber && chatItem.id) {
      navigate(`/claims/${claimNumber}/chat/${chatItem.id}`);
    }
  };

  return (
    <div className="__PAGE_BODY__ bg-gradient-3 flex-1 overflow-auto bg-cover px-20">
      <ResizeablePanels className="__MAIN_CONTAINER__ flex h-full">
        <ResizeablePanel defaultSize={60} minSize={40}>
          <div className="__LEFT_CONTAINER__ flex w-full justify-center overflow-auto">
            <div className="__LEFT_SCROLABLE__ flex w-full max-w-3xl flex-col gap-20 py-20">
              <ClaimListPanel<ClaimItemBase>
                title="Activity"
                items={documentItems}
                customRenderer={documentToListItem}
                onItemClick={(item) => toggleSelectedItem(item, 'document')}
                emptyState={EMPTY_STATES_BY_TYPE.document}
                isLoading={isDocumentsLoading}
                errorMessage={documentsError}
              />
              <ClaimListPanel<Insight>
                title="Insights"
                items={insights}
                customRenderer={insightToListItem}
                onItemClick={(item) => toggleSelectedItem(item, 'insight')}
                emptyState={EMPTY_STATES_BY_TYPE.insight}
                isLoading={isInsightsLoading}
                errorMessage={insightsError}
              />
              <ClaimListPanel<Action>
                title="Actions"
                items={actions}
                customRenderer={actionToListItem}
                onItemClick={(item) => toggleSelectedItem(item, 'action')}
                emptyState={EMPTY_STATES_BY_TYPE.action}
                isLoading={isActionsLoading}
                errorMessage={actionsError}
              />
              <ClaimListPanel<ClaimItemBase>
                title="Chats"
                items={chatItems}
                isLoading={isChatsLoading}
                errorMessage={chatsError}
                emptyState={EMPTY_STATES_BY_TYPE.chat}
                customRenderer={ChatToListItem}
                onItemClick={handleChatItemClick}
              />
              <div className="min-h-40"></div>
            </div>
          </div>
        </ResizeablePanel>
        {selectedItem && selectedItemType ? (
          <>
            <ResizeableHandle className="ml-20 w-4 gap-0 bg-transparent hover:bg-transparent" />
            <ResizeablePanel defaultSize={40} minSize={20}>
              <div className="__RIGHT_CONTAINER__ w-full overflow-auto">
                <div
                  className={cn(
                    '__RIGHT_SCROLLABLE__ h-full w-full py-20 transition-transform ease-in-out',
                    EFFECT_CONF.CSS_DURATION,
                    isAnimating ? 'translate-x-0' : 'translate-x-full'
                  )}
                >
                  {SIDE_PANELS_MAP[selectedItemType]?.(selectedItem)}
                </div>
              </div>
            </ResizeablePanel>
          </>
        ) : null}
      </ResizeablePanels>
    </div>
  );
};

export default ClaimPageBody;
